import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, FileText, BookOpen, Radio, Tv2,
  Menu, Bell, Megaphone, Layers, BarChart3,
  Newspaper, Users, Star, Briefcase, ClipboardList,
  Trophy, Building2, Target, MessageSquare,
  Image, UserCog, Settings, LogOut, ChevronRight,
  RefreshCw, Search, Plus, AlertTriangle, ExternalLink,
  ChevronDown, Cpu, Video
} from 'lucide-react';

// ── Sidebar nav groups ────────────────────────────────────────────────────────
const SIDEBAR = [
  {
    key: 'command',
    label: 'Command Publishing',
    items: [
      { label: 'Dashboard',        icon: LayoutDashboard, path: '/admin/dashboard' },
      { label: 'Content Hub',      icon: FileText,        path: '/admin/content' },
      { label: 'Article Engine',   icon: Cpu,             path: '/admin/article-engine' },
      { label: 'Magazine Manager', icon: BookOpen,        path: '/admin/magazine' },
      { label: 'First Round',      icon: Radio,           path: '/admin/first-round' },
      { label: 'Round Table',      icon: Radio,           path: '/admin/round-table' },
      { label: 'Ticker / Marquee', icon: Tv2,             path: '/admin/ticker' },
      { label: 'Video Manager',    icon: Video,           path: '/admin/videos' },
    ],
  },
  {
    key: 'placement',
    label: 'Placement Engine',
    items: [
      { label: 'Mega Menu',        icon: Menu,      path: '/admin/mega-menu' },
      { label: 'Hero Images',      icon: Image,     path: '/admin/hero-images' },
      { label: 'Announcement Bar', icon: Bell,      path: '/admin/settings' },
      { label: 'Banner Campaigns', icon: Megaphone, path: '/admin/banners' },
      { label: 'Widget Inventory', icon: Layers,    path: '/admin/widgets' },
      { label: 'Placement Slots',  icon: BarChart3, path: '/admin/placements' },
    ],
  },
  {
    key: 'audience',
    label: 'Audience',
    items: [
      { label: 'Newsletter',   icon: Newspaper, path: '/admin/newsletter' },
      { label: 'Subscribers',  icon: Users,     path: '/admin/subscribers' },
      { label: 'Sponsors',     icon: Star,      path: '/admin/sponsors' },
    ],
  },
  {
    key: 'career',
    label: 'Career Hub',
    items: [
      { label: 'Job Board Admin',   icon: Briefcase,     path: '/admin/job-board' },
      { label: 'Job Review Queue',  icon: Briefcase,     path: '/admin/jobs' },
      { label: 'Job Plans',         icon: ClipboardList, path: '/admin/job-plans' },
      { label: 'First Briefcase',   icon: Briefcase,     path: '/admin/first-briefcase' },
    ],
  },
  {
    key: 'people',
    label: 'People / Institutions',
    items: [
      { label: 'Exemplary Leaders',   icon: Trophy,    path: '/admin/exemplary' },
      { label: 'Institutions / Orgs', icon: Building2, path: '/admin/institutions' },
      { label: 'Focus Areas',         icon: Target,    path: '/admin/focus-areas' },
    ],
  },
  {
    key: 'inquiries',
    label: 'Inquiries',
    items: [
      { label: 'Contact / Inquiries', icon: MessageSquare, path: '/admin/contacts' },
      { label: 'Forms / Leads',       icon: ClipboardList, path: '/admin/leads' },
    ],
  },
  {
    key: 'system',
    label: 'System',
    items: [
      { label: 'Media / ImageKit', icon: Image,   path: '/admin/media' },
      { label: 'Users / Staff',    icon: UserCog, path: '/admin/users' },
      { label: 'Settings',         icon: Settings, path: '/admin/site-settings' },
    ],
  },
];

// Top bar #2 — main nav links (gold)
const TOP_NAV = [
  { label: 'Dashboard',  path: '/admin/dashboard' },
  { label: 'Editorial',  path: '/admin/content' },
  { label: 'Magazine',   path: '/admin/magazine' },
  { label: 'Career Hub', path: '/admin/jobs' },
  { label: 'Placements', path: '/admin/placements' },
  { label: 'Audience',   path: '/admin/newsletter' },
  { label: 'Analytics',  path: '/admin/site-settings' },
  { label: 'Settings',   path: '/admin/site-settings' },
];

// Top bar #2 — quick access tabs (left)
const QUICK_TABS = [
  { label: 'Dashboard',   path: '/admin/dashboard' },
  { label: 'Newsletters', path: '/admin/newsletter' },
  { label: 'Mega Menu',   path: '/admin/mega-menu' },
];

// ── Sub-components ────────────────────────────────────────────────────────────

function SidebarItem({ item, currentPath }) {
  const Icon = item.icon;
  const active =
    currentPath === item.path ||
    (item.path !== '/admin/dashboard' && currentPath.startsWith(item.path));

  return (
    <Link
      to={item.path}
      data-testid={`sidebar-${item.label.toLowerCase().replace(/[\s\/]+/g, '-')}`}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '6px 12px 6px 14px',
        margin: '1px 6px',
        borderRadius: 6,
        fontSize: 12,
        fontWeight: active ? 700 : 400,
        color: active ? '#16B8AE' : 'rgba(255,255,255,0.5)',
        background: active ? 'rgba(22,184,174,0.1)' : 'transparent',
        borderLeft: active ? '2px solid #16B8AE' : '2px solid transparent',
        textDecoration: 'none',
        transition: 'color .1s, background .1s',
        letterSpacing: '-.01em',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }}
    >
      <Icon size={13} style={{ flexShrink: 0, opacity: active ? 1 : 0.6 }} />
      <span>{item.label}</span>
    </Link>
  );
}

function SidebarGroup({ group, currentPath, collapsed, onToggle }) {
  return (
    <div style={{ marginBottom: 2 }}>
      <button
        onClick={onToggle}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
          padding: '7px 14px 4px',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          fontSize: 9,
          fontWeight: 800,
          letterSpacing: '.1em',
          textTransform: 'uppercase',
          color: 'rgba(255,255,255,0.22)',
        }}
      >
        <span>{group.label}</span>
        <ChevronDown
          size={10}
          style={{ opacity: 0.4, transform: collapsed ? 'rotate(-90deg)' : 'none', transition: 'transform .15s' }}
        />
      </button>
      {!collapsed && group.items.map(item => (
        <SidebarItem key={item.path} item={item} currentPath={currentPath} />
      ))}
    </div>
  );
}

// ── Main export ───────────────────────────────────────────────────────────────

export function AdminLayout({ children, title, actions }) {
  const location = useLocation();
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const [collapsed, setCollapsed] = useState({});

  const toggleGroup = (key) => setCollapsed(c => ({ ...c, [key]: !c[key] }));

  const handleLogout = () => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user');
    navigate('/admin/login');
  };

  const currentPath = location.pathname;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', fontFamily: "'Inter', sans-serif" }}>

      {/* ── TOP BAR 1 — Identity / Brand ────────────────────────────── */}
      <div
        data-testid="topbar-identity"
        style={{
          height: 44,
          background: '#070F1C',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0 18px',
          flexShrink: 0,
          zIndex: 60,
          borderBottom: '1px solid rgba(255,255,255,0.05)',
        }}
      >
        {/* Brand */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{
            width: 28, height: 28, borderRadius: 6,
            background: 'linear-gradient(135deg,#008C86,#04243A)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 10, fontWeight: 900, color: '#fff', letterSpacing: '-.02em',
            border: '1px solid rgba(0,140,134,.4)', flexShrink: 0,
          }}>OP</div>
          <span style={{ fontSize: 13, fontWeight: 700, color: '#fff', letterSpacing: '-.03em' }}>
            Opportunity Post
          </span>
          <span style={{ fontSize: 9, fontWeight: 700, color: 'rgba(255,255,255,0.28)', letterSpacing: '.1em', textTransform: 'uppercase', marginLeft: 2 }}>
            CMS
          </span>
        </div>

        {/* Right controls */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <a href="/" target="_blank" rel="noopener noreferrer"
            style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: 'rgba(255,255,255,0.35)', textDecoration: 'none' }}>
            <ExternalLink size={11} /> Public Site
          </a>
          <div style={{ width: 1, height: 16, background: 'rgba(255,255,255,0.1)' }} />
          <button style={{ background: 'none', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 6, padding: '4px 10px', cursor: 'pointer', color: 'rgba(255,255,255,0.5)', fontSize: 11, display: 'flex', alignItems: 'center', gap: 5 }}>
            <AlertTriangle size={11} /> Alerts
          </button>
          <button
            data-testid="topbar-new-contact-btn"
            style={{ background: '#C8951A', border: 'none', borderRadius: 6, padding: '5px 12px', cursor: 'pointer', color: '#fff', fontSize: 11, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 5 }}>
            <Plus size={11} /> New Inquiry
          </button>
          <button
            onClick={handleLogout}
            data-testid="topbar-logout-btn"
            title={`Logout ${user.name || ''}`}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.3)', padding: '4px 6px', display: 'flex', alignItems: 'center', borderRadius: 5 }}>
            <LogOut size={13} />
          </button>
        </div>
      </div>

      {/* ── TOP BAR 2 — Main CMS Navigation ─────────────────────────── */}
      <div
        data-testid="topbar-nav"
        style={{
          height: 44,
          background: '#0A1828',
          display: 'flex',
          alignItems: 'stretch',
          justifyContent: 'space-between',
          padding: '0 18px',
          flexShrink: 0,
          zIndex: 59,
          borderBottom: '1px solid rgba(255,255,255,0.06)',
        }}
      >
        {/* Quick-access tabs */}
        <div style={{ display: 'flex', alignItems: 'stretch', gap: 0 }}>
          {QUICK_TABS.map(tab => {
            const active = currentPath === tab.path || (tab.path !== '/admin/dashboard' && currentPath.startsWith(tab.path));
            return (
              <Link key={tab.path} to={tab.path} style={{
                display: 'flex', alignItems: 'center',
                padding: '0 16px',
                fontSize: 13, fontWeight: active ? 700 : 500,
                color: active ? '#fff' : 'rgba(255,255,255,0.4)',
                textDecoration: 'none',
                borderBottom: active ? '2px solid #16B8AE' : '2px solid transparent',
                transition: 'color .12s, border-color .12s',
              }}>
                {tab.label}
              </Link>
            );
          })}
        </div>

        {/* Gold top-level section links */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
          {TOP_NAV.map((item, i) => {
            const active = currentPath === item.path || (item.path !== '/admin/dashboard' && currentPath.startsWith(item.path));
            return (
              <React.Fragment key={item.label}>
                {i > 0 && <span style={{ color: 'rgba(255,255,255,0.15)', fontSize: 11, margin: '0 2px' }}>|</span>}
                <Link to={item.path} style={{
                  fontSize: 11, fontWeight: active ? 800 : 600,
                  color: active ? '#E8A92A' : '#C8951A',
                  textDecoration: 'none',
                  padding: '0 8px',
                  opacity: active ? 1 : 0.8,
                  transition: 'opacity .1s',
                }}>
                  {item.label}
                </Link>
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {/* ── TOP BAR 3 — Action Toolbar ───────────────────────────────── */}
      <div
        data-testid="topbar-actions"
        style={{
          height: 40,
          background: '#0D1E35',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0 18px',
          flexShrink: 0,
          zIndex: 58,
          borderBottom: '1px solid rgba(255,255,255,0.04)',
        }}
      >
        {/* Breadcrumb */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: 'rgba(255,255,255,0.3)' }}>
          <span style={{ color: 'rgba(255,255,255,0.18)' }}>CMS</span>
          <ChevronRight size={10} style={{ opacity: 0.3 }} />
          <span style={{ color: 'rgba(255,255,255,0.6)', fontWeight: 600 }}>{title || 'Dashboard'}</span>
        </div>

        {/* Page-level actions — passed as prop, or default refresh/search */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {actions || (
            <>
              <button
                onClick={() => window.location.reload()}
                style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '5px 12px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 6, cursor: 'pointer', color: 'rgba(255,255,255,0.55)', fontSize: 11, fontWeight: 600 }}>
                <RefreshCw size={11} /> Refresh
              </button>
              <button
                style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '5px 12px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 6, cursor: 'pointer', color: 'rgba(255,255,255,0.55)', fontSize: 11, fontWeight: 600 }}>
                <Search size={11} /> Search
              </button>
            </>
          )}
        </div>
      </div>

      {/* ── Body ─────────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', flex: 1, overflow: 'hidden', minHeight: 0 }}>

        {/* ── LEFT SIDEBAR ─────────────────────────────────────────── */}
        <aside
          data-testid="admin-sidebar"
          style={{
            width: 210,
            minWidth: 210,
            background: '#07101F',
            display: 'flex',
            flexDirection: 'column',
            overflowY: 'auto',
            overflowX: 'hidden',
            flexShrink: 0,
            scrollbarWidth: 'none',
            borderRight: '1px solid rgba(255,255,255,0.04)',
          }}
        >
          <div style={{ paddingTop: 8, paddingBottom: 24 }}>
            {SIDEBAR.map(group => (
              <SidebarGroup
                key={group.key}
                group={group}
                currentPath={currentPath}
                collapsed={!!collapsed[group.key]}
                onToggle={() => toggleGroup(group.key)}
              />
            ))}
          </div>
        </aside>

        {/* ── MAIN CONTENT ─────────────────────────────────────────── */}
        <main
          style={{
            flex: 1,
            overflowY: 'auto',
            background: '#0D1E35',
            minWidth: 0,
          }}
        >
          {children}
        </main>
      </div>
    </div>
  );
}
